import { useState, useEffect, useCallback, useMemo } from 'react';
import { HELP_ARTICLES } from '../config/onboardingMedia';

/**
 * Feature usage weights for recommendation scoring
 */
const FEATURE_WEIGHTS = {
  // Core features (higher weight = more important)
  dashboard: { weight: 10, category: 'core' },
  search: { weight: 8, category: 'core' },
  'quick-actions': { weight: 6, category: 'core' },

  // Budget features
  'budget-transfers': { weight: 9, category: 'budget' },
  'budget-scenarios': { weight: 7, category: 'budget' },
  'program-margins': { weight: 6, category: 'budget' },

  // Research features
  'research-portfolio': { weight: 8, category: 'research' },
  'research-commitments': { weight: 7, category: 'research' },

  // HR features
  positions: { weight: 8, category: 'hr' },
  'personnel-whatif': { weight: 7, category: 'hr' },

  // Admin features
  admin: { weight: 9, category: 'admin' },
};

/**
 * usePersonalizedRecommendations - Hook for personalized feature recommendations
 *
 * Analyzes user behavior and suggests features they haven't explored yet.
 * Prioritizes recommendations based on role, usage patterns, and feature importance.
 */
export function usePersonalizedRecommendations(userId, userRolesProp = []) {
  const [featureUsage, setFeatureUsage] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Memoize userRoles to prevent infinite re-renders
  // Compare by serialized value since arrays are passed by reference
  const userRolesKey = JSON.stringify(userRolesProp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userRoles = useMemo(() => userRolesProp || [], [userRolesKey]);

  // Load feature usage from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`feature_usage_${userId}`);
      if (stored) {
        setFeatureUsage(JSON.parse(stored));
      }
    } catch {
      setFeatureUsage({});
    }
    setLoading(false);
  }, [userId]);

  // Save feature usage
  const saveUsage = useCallback((usage) => {
    try {
      localStorage.setItem(`feature_usage_${userId}`, JSON.stringify(usage));
    } catch {
      // Ignore
    }
  }, [userId]);

  // Track feature usage
  const trackFeatureUsage = useCallback((featureId, duration = 0) => {
    setFeatureUsage(prev => {
      const current = prev[featureId] || { count: 0, totalDuration: 0, lastUsed: null };
      const updated = {
        ...prev,
        [featureId]: {
          count: current.count + 1,
          totalDuration: current.totalDuration + duration,
          lastUsed: new Date().toISOString(),
          firstUsed: current.firstUsed || new Date().toISOString(),
        },
      };
      saveUsage(updated);
      return updated;
    });
  }, [saveUsage]);

  // Calculate recommendation score for a feature
  const calculateScore = useCallback((featureId) => {
    const config = FEATURE_WEIGHTS[featureId];
    if (!config) return 0;

    const usage = featureUsage[featureId];
    let score = config.weight * 10; // Base score from weight

    if (!usage) {
      // Feature not used yet - high priority
      score += 50;
    } else {
      // Reduce score based on usage
      score -= Math.min(usage.count * 5, 30);

      // Time since last use (higher score if not used recently)
      if (usage.lastUsed) {
        const daysSinceUse = (Date.now() - new Date(usage.lastUsed).getTime()) / (1000 * 60 * 60 * 24);
        score += Math.min(daysSinceUse * 2, 20);
      }
    }

    // Role-based boost
    const roleCategories = {
      budget_owner: 'budget',
      finance: 'budget',
      researcher: 'research',
      pi: 'research',
      hr: 'hr',
      admin: 'admin',
    };

    const userCategories = userRoles
      .map(role => roleCategories[role.toLowerCase()])
      .filter(Boolean);

    if (userCategories.includes(config.category)) {
      score += 20; // Boost for role-relevant features
    }

    return Math.max(0, score);
  }, [featureUsage, userRoles]);

  // Generate recommendations - memoize to prevent unnecessary state updates
  const scoredRecommendations = useMemo(() => {
    if (loading) return null;

    return Object.keys(FEATURE_WEIGHTS)
      .map(featureId => ({
        featureId,
        score: calculateScore(featureId),
        usage: featureUsage[featureId],
        ...FEATURE_WEIGHTS[featureId],
        helpArticle: HELP_ARTICLES[featureId],
      }))
      .filter(item => item.score > 20) // Only show features with meaningful scores
      .sort((a, b) => b.score - a.score)
      .slice(0, 5); // Top 5 recommendations
  }, [featureUsage, loading, calculateScore]);

  // Update recommendations state only when scored recommendations change
  useEffect(() => {
    if (scoredRecommendations !== null) {
      setRecommendations(scoredRecommendations);
    }
  }, [scoredRecommendations]);

  // Get recommendation for a specific feature
  const getRecommendation = useCallback((featureId) => {
    return recommendations.find(r => r.featureId === featureId);
  }, [recommendations]);

  // Check if a feature is recommended
  const isRecommended = useCallback((featureId) => {
    return recommendations.some(r => r.featureId === featureId);
  }, [recommendations]);

  // Get unused features
  const unusedFeatures = useMemo(() => {
    return Object.keys(FEATURE_WEIGHTS)
      .filter(featureId => !featureUsage[featureId])
      .map(featureId => ({
        featureId,
        ...FEATURE_WEIGHTS[featureId],
        helpArticle: HELP_ARTICLES[featureId],
      }));
  }, [featureUsage]);

  // Get feature usage stats
  const getUsageStats = useCallback((featureId) => {
    return featureUsage[featureId] || null;
  }, [featureUsage]);

  // Get overall engagement score
  const engagementScore = useMemo(() => {
    const totalFeatures = Object.keys(FEATURE_WEIGHTS).length;
    const usedFeatures = Object.keys(featureUsage).length;
    const usagePercentage = (usedFeatures / totalFeatures) * 100;

    // Factor in usage frequency
    const totalUsage = Object.values(featureUsage).reduce((sum, u) => sum + u.count, 0);
    const avgUsage = usedFeatures > 0 ? totalUsage / usedFeatures : 0;
    const frequencyBonus = Math.min(avgUsage * 5, 25);

    return Math.min(100, Math.round(usagePercentage + frequencyBonus));
  }, [featureUsage]);

  // Get recommendations by category
  const getByCategory = useCallback((category) => {
    return recommendations.filter(r => r.category === category);
  }, [recommendations]);

  return {
    // State
    recommendations,
    unusedFeatures,
    featureUsage,
    engagementScore,
    loading,

    // Actions
    trackFeatureUsage,
    getRecommendation,
    isRecommended,
    getUsageStats,
    getByCategory,
  };
}

export default usePersonalizedRecommendations;
